// pages/CreateContent.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Grid,
  InputAdornment,
  Divider,
} from '@mui/material';
import { 
  Save, 
  Publish, 
  Lightbulb, 
  School, 
  RateReview, 
  Science, 
  Psychology,
  EditNote,
  Description,
  AutoAwesome,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AdvancedRichTextEditor from '../../components/AdvancedRichTextEditor';

const CreateContent = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Draft',
    tags: [],
    status: 'draft',
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Categories for content library with icons
  const categories = [
    { value: 'Draft', label: 'Draft', icon: <EditNote />, color: '#9E9E9E', description: 'Work in progress' },
    { value: 'Idea', label: 'Idea', icon: <Lightbulb />, color: '#FFC107', description: 'Brainstormed concepts' },
    { value: 'Research', label: 'Research', icon: <Science />, color: '#2196F3', description: 'Research materials' },
    { value: 'Tutorial', label: 'Tutorial', icon: <School />, color: '#4CAF50', description: 'Step-by-step guides' },
    { value: 'Review', label: 'Review', icon: <RateReview />, color: '#FF9800', description: 'Product/service reviews' },
    { value: 'Opinion', label: 'Opinion', icon: <Psychology />, color: '#9C27B0', description: 'Personal perspectives' },
    { value: 'Interview', label: 'Interview', icon: <Description />, color: '#00BCD4', description: 'Q&A sessions' },
    { value: 'Case Study', label: 'Case Study', icon: <AutoAwesome />, color: '#E91E63', description: 'In-depth analysis' },
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth/sign-in');
    }
    
    // Check for imported content
    if (location.state?.importedContent) {
      const imported = location.state.importedContent;
      setFormData({
        title: imported.title || '',
        content: imported.content || '',
        excerpt: imported.excerpt || '',
        category: imported.category || 'Draft',
        tags: imported.tags || [],
        status: 'draft',
      });
      // Clear location state
      window.history.replaceState({}, document.title);
    }
  }, [currentUser, navigate, location]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    
    if (!trimmedTag) return;
    
    // Split tags by comma
    const newTags = trimmedTag.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    // Filter out empty tags and duplicates
    const uniqueTags = [...new Set([...formData.tags, ...newTags])];
    
    // Remove any emojis or special characters (allow letters, numbers, spaces, hyphens)
    const cleanTags = uniqueTags.map(tag => 
      tag.replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim()
    ).filter(tag => tag.length > 0 && tag.length <= 30);
    
    if (cleanTags.length > 0) {
      setFormData({
        ...formData,
        tags: cleanTags,
      });
      setTagInput('');
      setError('');
    }
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove),
    });
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!formData.title.trim()) {
      setTitleError('Please enter a title');
      isValid = false;
    } else if (formData.title.length < 3) {
      setTitleError('Title must be at least 3 characters');
      isValid = false;
    } else if (formData.title.length > 150) {
      setTitleError('Title must be less than 150 characters');
      isValid = false;
    } else {
      setTitleError('');
    }
    
    if (!formData.content.trim()) {
      setContentError('Please enter content');
      isValid = false;
    } else if (formData.content.length < 20) {
      setContentError('Content must be at least 20 characters');
      isValid = false;
    } else {
      setContentError('');
    }
    
    return isValid;
  };

  const handleSubmit = async (e, publishImmediately = false) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate excerpt from content if not provided
      let excerpt = formData.excerpt.trim();
      if (!excerpt) {
        // Strip HTML tags and get plain text
        const plainText = formData.content.replace(/<[^>]*>/g, '');
        excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
      }

      const contentData = {
        title: formData.title.trim(),
        content: formData.content,
        excerpt: excerpt,
        category: formData.category,
        tags: formData.tags,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        author: currentUser.displayName || currentUser.email,
        authorAvatar: currentUser.photoURL || null,
        status: publishImmediately ? 'published' : 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        wordCount: formData.content.replace(/<[^>]*>/g, '').split(/\s+/).length,
        characterCount: formData.content.replace(/<[^>]*>/g, '').length,
      };

      await addDoc(collection(db, 'contents'), contentData);
      
      setSuccess(publishImmediately 
        ? 'Content published successfully! Redirecting...' 
        : 'Content saved as draft! Redirecting...'
      );
      
      setTimeout(() => {
        navigate('/admin/content');
      }, 1500);
    } catch (err) {
      console.error('Error creating content:', err);
      setError('Failed to create content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getWordCount = () => {
    const plainText = formData.content.replace(/<[^>]*>/g, '');
    const words = plainText.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 1 }}>
          Create New Content
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create content that can be imported to blogs later. Use rich formatting, images, and more.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={(e) => handleSubmit(e, false)}>
          {/* Title Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Content Title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (titleError) setTitleError('');
              }}
              required
              variant="outlined"
              placeholder="Enter a descriptive title for your content..."
              error={!!titleError}
              helperText={titleError || `${formData.title.length}/150 characters`}
              inputProps={{ maxLength: 150 }}
            />
          </Box>

          {/* Category Selection with Icons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Content Category
            </Typography>
            <Grid container spacing={1}>
              {categories.map((cat) => (
                <Grid item key={cat.value}>
                  <Button
                    variant={formData.category === cat.value ? 'contained' : 'outlined'}
                    onClick={() => setFormData({ ...formData, category: cat.value })}
                    startIcon={cat.icon}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      borderColor: formData.category === cat.value ? cat.color : undefined,
                      backgroundColor: formData.category === cat.value ? cat.color : undefined,
                      '&:hover': {
                        backgroundColor: formData.category === cat.value ? cat.color : undefined,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {cat.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {categories.find(cat => cat.value === formData.category)?.description}
            </Typography>
          </Box>

          {/* Excerpt Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Excerpt (Optional)"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              multiline
              rows={2}
              variant="outlined"
              placeholder="A short summary of your content (will be auto-generated from content if left empty)..."
              helperText={`${formData.excerpt.length}/300 characters`}
              inputProps={{ maxLength: 300 }}
            />
          </Box>

          {/* Tags Input with Comma Separation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Tags
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Add tags separated by commas (e.g., React, JavaScript, Web Development)..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagInputKeyPress}
              onBlur={handleAddTag}
              helperText="Press Enter, comma, or click Add Tag to add multiple tags"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      variant="text" 
                      onClick={handleAddTag}
                      size="small"
                    >
                      Add Tag
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            {formData.tags.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Content Editor */}
          <Box sx={{ mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Content
              </Typography>
              <Box display="flex" gap={2}>
                <Typography variant="caption" color="textSecondary">
                  Words: {getWordCount()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Characters: {formData.content.replace(/<[^>]*>/g, '').length}
                </Typography>
              </Box>
            </Box>
            <AdvancedRichTextEditor
              value={formData.content}
              onChange={(content) => {
                setFormData({ ...formData, content });
                if (contentError) setContentError('');
              }}
              placeholder="Write your content here. You can use formatting tools, add images, tables, code blocks, and more..."
            />
            {contentError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {contentError}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => navigate('/admin/content')}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outlined"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Save as Draft
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Publish />}
              disabled={loading}
              onClick={(e) => handleSubmit(e, true)}
              sx={{ borderRadius: 2 }}
            >
              Publish Now
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateContent;